// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
// Proprietary and confidential. See LICENSE file in project root.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import './PortfolioEditor.css';

/**
 * PortfolioEditor — persistent, editable holdings that live in the DB
 * (UserPortfolio table). This is the source of truth Alpha reads via
 * fetch_my_portfolio, and what pre-loads the analysis flow.
 *
 * Two tables: equities (Company / Ticker / Avg Cost / Shares) and digital
 * assets (Coin / Ticker / Avg Cost / # Coins). Saving POSTs the combined
 * set to /api/portfolio/set, which replaces the user's saved positions.
 */

// A small map so common coins show a friendly name without a crypto-name
// lookup service. Falls back to the ticker for anything not listed.
const COIN_NAMES = {
  BTC: 'Bitcoin', ETH: 'Ethereum', SOL: 'Solana', XRP: 'XRP',
  ADA: 'Cardano', DOGE: 'Dogecoin', AVAX: 'Avalanche', LINK: 'Chainlink',
  DOT: 'Polkadot', MATIC: 'Polygon', LTC: 'Litecoin', BCH: 'Bitcoin Cash',
  UNI: 'Uniswap', ATOM: 'Cosmos', XLM: 'Stellar', ALGO: 'Algorand',
};

let _rowIdSeq = 1;
const newRow = (overrides = {}) => ({
  _id: `row-${_rowIdSeq++}`,
  symbol: '',
  name: '',
  avg_cost: '',
  shares: '',
  ...overrides,
});

// Supported precious metals (valued at live spot/oz — never scored).
const METAL_OPTIONS = [
  { key: 'gold', label: 'Gold' },
  { key: 'silver', label: 'Silver' },
  { key: 'platinum', label: 'Platinum' },
  { key: 'palladium', label: 'Palladium' },
];

let _metalIdSeq = 1;
const newMetalRow = (overrides = {}) => ({
  _id: `metal-${_metalIdSeq++}`,
  metal: 'gold',
  ounces: '',
  ...overrides,
});

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export default function PortfolioEditor({ onSaved, onAnalyze }) {
  const [equityRows, setEquityRows] = useState([]);
  const [cryptoRows, setCryptoRows] = useState([]);
  const [cash, setCash] = useState('');
  const [metalRows, setMetalRows] = useState([]);
  const [metalSpot, setMetalSpot] = useState({}); // {gold: {name, spot}, ...}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // {type: 'ok'|'err', msg}
  const [collapsed, setCollapsed] = useState(false);

  // Per-row debounce timers for ticker → name resolution.
  const nameTimers = useRef({});

  // ── Load saved positions on mount ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/portfolio/positions');
        if (cancelled) return;
        const positions = data?.positions || [];
        const eq = [];
        const cr = [];
        for (const p of positions) {
          const row = newRow({
            symbol: p.symbol,
            avg_cost: p.entry_price ? String(p.entry_price) : '',
            shares: p.shares ? String(p.shares) : '',
          });
          if (p.is_crypto) {
            row.name = COIN_NAMES[p.symbol] || p.symbol;
            cr.push(row);
          } else {
            eq.push(row);
          }
        }
        setEquityRows(eq);
        setCryptoRows(cr);
        // Resolve equity names in the background (one search call each, cached
        // by the browser; only a handful of positions in practice).
        eq.forEach((r) => resolveName(r._id, r.symbol, false));
      } catch (err) {
        if (!cancelled) setStatus({ type: 'err', msg: 'Could not load saved portfolio.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load saved cash/metals + live metal spot prices on mount ──
  useEffect(() => {
    let cancelled = false;
    api.get('/portfolio/other-holdings')
      .then(({ data }) => {
        if (cancelled || !data) return;
        if (data.cash > 0) setCash(String(data.cash));
        const saved = data.metals_input || [];
        if (saved.length) {
          setMetalRows(saved.map((m) => newMetalRow({ metal: m.metal, ounces: String(m.ounces) })));
        }
      })
      .catch(() => { /* anonymous or none saved — fine */ });
    api.get('/portfolio/metal-spot')
      .then(({ data }) => { if (!cancelled && data?.metals) setMetalSpot(data.metals); })
      .catch(() => { /* spot prices are best-effort */ });
    return () => { cancelled = true; };
  }, []);

  // ── Ticker → company name resolution via existing /market/search ──
  const resolveName = useCallback(async (rowId, symbol, isCrypto) => {
    const sym = (symbol || '').trim().toUpperCase();
    if (!sym) return;
    if (isCrypto) {
      const nm = COIN_NAMES[sym] || sym;
      setCryptoRows((rows) => rows.map((r) => (r._id === rowId ? { ...r, name: nm } : r)));
      return;
    }
    try {
      const { data } = await api.get(`/market/search?q=${encodeURIComponent(sym)}&limit=5`);
      // Only accept an EXACT ticker match — never fall back to the first hit, or a
      // half-typed symbol (e.g. "R" while typing "ROK") resolves to a real but wrong
      // company (Ryder System, ticker R) and can stick.
      const match = (data || []).find((d) => (d.symbol || '').toUpperCase() === sym);
      if (match) {
        setEquityRows((rows) =>
          rows.map((r) => {
            if (r._id !== rowId) return r;
            // Guard against a stale/out-of-order response: if the user has since
            // changed the symbol, don't let the old lookup overwrite the name.
            if ((r.symbol || '').trim().toUpperCase() !== sym) return r;
            return { ...r, name: match.name || '' };
          })
        );
      }
    } catch {
      /* name resolution is best-effort */
    }
  }, []);

  const handleSymbolChange = (rowId, isCrypto, value) => {
    const setter = isCrypto ? setCryptoRows : setEquityRows;
    setter((rows) => rows.map((r) => (r._id === rowId ? { ...r, symbol: value } : r)));
    // Debounce name resolution per row.
    if (nameTimers.current[rowId]) clearTimeout(nameTimers.current[rowId]);
    nameTimers.current[rowId] = setTimeout(() => resolveName(rowId, value, isCrypto), 400);
  };

  const handleFieldChange = (rowId, isCrypto, field, value) => {
    const setter = isCrypto ? setCryptoRows : setEquityRows;
    setter((rows) => rows.map((r) => (r._id === rowId ? { ...r, [field]: value } : r)));
  };

  const addRow = (isCrypto) => {
    (isCrypto ? setCryptoRows : setEquityRows)((rows) => [...rows, newRow()]);
  };

  const removeRow = (rowId, isCrypto) => {
    (isCrypto ? setCryptoRows : setEquityRows)((rows) => rows.filter((r) => r._id !== rowId));
  };

  // ── Precious-metals row helpers ──
  const addMetalRow = () => setMetalRows((rows) => [...rows, newMetalRow()]);
  const removeMetalRow = (rowId) =>
    setMetalRows((rows) => rows.filter((r) => r._id !== rowId));
  const handleMetalChange = (rowId, field, value) =>
    setMetalRows((rows) => rows.map((r) => (r._id === rowId ? { ...r, [field]: value } : r)));

  const metalValue = (row) => {
    const oz = parseFloat(row.ounces);
    const spot = metalSpot[row.metal]?.spot;
    if (!oz || oz <= 0 || !spot) return null;
    return oz * spot;
  };
  const totalMetalsValue = metalRows.reduce((sum, r) => sum + (metalValue(r) || 0), 0);
  const cashValue = parseFloat(cash) > 0 ? parseFloat(cash) : 0;

  // ── Build the value-only holdings payload (cash + metals) ──
  const buildOtherHoldings = () => ({
    cash: cashValue,
    metals: metalRows
      .filter((r) => r.metal && parseFloat(r.ounces) > 0)
      .map((r) => ({ metal: r.metal, ounces: parseFloat(r.ounces) })),
  });

  // ── Build a validated positions list from both tables, or null on error ──
  const buildPositions = () => {
    const all = [...equityRows, ...cryptoRows];
    const positions = [];
    for (const r of all) {
      const sym = (r.symbol || '').trim().toUpperCase();
      if (!sym) continue;
      const shares = parseFloat(r.shares);
      const cost = parseFloat(r.avg_cost);
      if (!shares || shares <= 0) {
        setStatus({ type: 'err', msg: `${sym}: enter a share/coin count greater than 0.` });
        return null;
      }
      positions.push({
        symbol: sym,
        shares,
        entry_price: Number.isFinite(cost) ? cost : 0,
      });
    }
    return positions;  // may be empty; caller decides if that's an error
  };

  // ── Save: persist cash/metals + positions ──
  // Cash/metals are saved even with no equity/crypto positions (a user may hold
  // only cash + metals). Positions save only when present.
  const handleSave = async ({ alsoAnalyze = false } = {}) => {
    setStatus(null);
    const positions = buildPositions();
    if (positions === null) return;  // a row failed validation

    const other = buildOtherHoldings();
    const hasOther = other.cash > 0 || other.metals.length > 0;

    if (positions.length === 0 && !hasOther) {
      setStatus({ type: 'err', msg: 'Add at least one position, or some cash/metals, before saving.' });
      return;
    }

    setSaving(true);
    try {
      // Persist value-only holdings (cash + metals) — BEST-EFFORT so a hiccup
      // here can never block saving the actual positions.
      try {
        await api.post('/portfolio/other-holdings', other);
      } catch (ohErr) {
        console.warn('Cash/metals save failed (non-blocking):', ohErr);
      }
      if (positions.length > 0) {
        await api.post('/portfolio/set', { positions });
      }

      const parts = [];
      if (positions.length > 0) parts.push(`${positions.length} position${positions.length === 1 ? '' : 's'}`);
      if (other.cash > 0) parts.push('cash');
      if (other.metals.length > 0) parts.push('metals');
      const savedWhat = parts.join(' + ') || 'holdings';
      setStatus({
        type: 'ok',
        msg: alsoAnalyze
          ? `Saved ${savedWhat}. Running analysis…`
          : `Saved ${savedWhat}. Alpha can now see your full portfolio.`,
      });
      if (onSaved) onSaved(positions);
      if (alsoAnalyze && onAnalyze) {
        // Map to the shape the analyzer expects. Don't await — let analysis run.
        // Cash/metals are pulled server-side from the saved holdings we just wrote.
        onAnalyze(positions.map((p) => ({
          symbol: p.symbol,
          shares: p.shares,
          entry_price: p.entry_price,
        })));
      }
    } catch (err) {
      // Surface the REAL reason (status + message) instead of a generic string,
      // so a failure is self-diagnosing (e.g. "Save failed (403)…" = Cloudflare,
      // "Save failed: Network Error" = blocked/offline/stale client).
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const detailMsg = typeof detail === 'string' ? detail : detail?.message;
      const reason = detailMsg || err?.message || 'Unknown error';
      setStatus({
        type: 'err',
        msg: status ? `Save failed (HTTP ${status}): ${reason}` : `Save failed: ${reason}. If this persists, hard-refresh / clear this site’s cache.`,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderTable = (rows, isCrypto) => {
    const nameLabel = isCrypto ? 'Coin' : 'Company';
    const tickerLabel = isCrypto ? 'Coin Ticker' : 'Ticker';
    const costLabel = isCrypto ? 'Avg Cost / Coin' : 'Avg Cost / Share';
    const qtyLabel = isCrypto ? '# Coins' : 'Shares';
    return (
      <div className="pe-table-wrap">
        <div className="pe-table-title">{isCrypto ? 'Digital Assets' : 'Equities'}</div>
        <div className="pe-table" role="table">
          <div className="pe-row pe-row-head" role="row">
            <span className="pe-col pe-col-name">{nameLabel}</span>
            <span className="pe-col pe-col-ticker">{tickerLabel}</span>
            <span className="pe-col pe-col-num">{costLabel}</span>
            <span className="pe-col pe-col-num">{qtyLabel}</span>
            <span className="pe-col pe-col-del" />
          </div>
          {rows.length === 0 && (
            <div className="pe-empty-row">No {isCrypto ? 'digital assets' : 'equities'} yet — add one below.</div>
          )}
          {rows.map((r) => (
            <div className="pe-row" role="row" key={r._id}>
              <span className="pe-col pe-col-name pe-name-display" title={r.name}>
                {r.name || <span className="pe-name-placeholder">—</span>}
              </span>
              <input
                className="pe-col pe-col-ticker pe-input"
                value={r.symbol}
                onChange={(e) => handleSymbolChange(r._id, isCrypto, e.target.value.toUpperCase())}
                placeholder={isCrypto ? 'BTC' : 'NVDA'}
                maxLength={12}
                aria-label={tickerLabel}
              />
              <span className="pe-col pe-col-num pe-cost-cell">
                <span className="pe-cost-prefix">$</span>
                <input
                  className="pe-input pe-cost-input"
                  value={r.avg_cost}
                  onChange={(e) => handleFieldChange(r._id, isCrypto, 'avg_cost', e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  aria-label={costLabel}
                />
              </span>
              <input
                className="pe-col pe-col-num pe-input"
                value={r.shares}
                onChange={(e) => handleFieldChange(r._id, isCrypto, 'shares', e.target.value)}
                placeholder="0"
                inputMode="decimal"
                aria-label={qtyLabel}
              />
              <button
                className="pe-col pe-col-del pe-del-btn"
                onClick={() => removeRow(r._id, isCrypto)}
                aria-label="Remove position"
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button className="pe-add-btn" onClick={() => addRow(isCrypto)}>
          + Add {isCrypto ? 'digital asset' : 'equity'}
        </button>
      </div>
    );
  };

  return (
    <div className="portfolio-editor">
      <div className="pe-header" onClick={() => setCollapsed((c) => !c)}>
        <div>
          <h3 className="pe-title">My Portfolio</h3>
          <p className="pe-subtitle">
            {/* Led with Alpha until 2026-09-11, which means nothing to a new user
                who has not met Alpha yet. Lead with what the page is for. */}
            Add a ticker, how many shares, and what you paid. Your holdings are saved,
            so you can grade them again any day and see what changed.
          </p>
        </div>
        <button className="pe-collapse-btn" aria-label={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? '▶' : '▼'}
        </button>
      </div>

      {!collapsed && (
        <div className="pe-body">
          {loading ? (
            <div className="pe-loading">Loading your portfolio…</div>
          ) : (
            <>
              {renderTable(equityRows, false)}
              {renderTable(cryptoRows, true)}

              {/* ── Cash & Precious Metals (value-only — not scored) ── */}
              <div className="pe-table-wrap pe-other-wrap">
                <div className="pe-table-title">Cash &amp; Precious Metals</div>
                <p className="pe-other-note">
                  Counted in your total value and allocation, but not graded like stocks
                  or digital assets. Metals are valued at live spot price per troy ounce.
                </p>

                <div className="pe-cash-row">
                  <span className="pe-cash-label">Deployable cash</span>
                  <span className="pe-cost-cell pe-cash-input-cell">
                    <span className="pe-cost-prefix">$</span>
                    <input
                      className="pe-input pe-cost-input"
                      value={cash}
                      onChange={(e) => setCash(e.target.value)}
                      placeholder="0.00"
                      inputMode="decimal"
                      aria-label="Deployable cash"
                    />
                  </span>
                </div>

                <div className="pe-table" role="table">
                  <div className="pe-row pe-row-head pe-metal-row" role="row">
                    <span className="pe-col">Metal</span>
                    <span className="pe-col">Ounces</span>
                    <span className="pe-col">Spot / oz</span>
                    <span className="pe-col">Value</span>
                    <span className="pe-col pe-col-del" />
                  </div>
                  {metalRows.length === 0 && (
                    <div className="pe-empty-row">No precious metals yet — add one below.</div>
                  )}
                  {metalRows.map((r) => {
                    const spot = metalSpot[r.metal]?.spot;
                    const val = metalValue(r);
                    return (
                      <div className="pe-row pe-metal-row" role="row" key={r._id}>
                        <select
                          className="pe-input pe-col"
                          value={r.metal}
                          onChange={(e) => handleMetalChange(r._id, 'metal', e.target.value)}
                          aria-label="Metal"
                        >
                          {METAL_OPTIONS.map((m) => (
                            <option key={m.key} value={m.key}>{m.label}</option>
                          ))}
                        </select>
                        <input
                          className="pe-input pe-col"
                          value={r.ounces}
                          onChange={(e) => handleMetalChange(r._id, 'ounces', e.target.value)}
                          placeholder="0.00"
                          inputMode="decimal"
                          aria-label="Ounces"
                        />
                        <span className="pe-col pe-metal-readout">
                          {spot ? formatCurrency(spot) : '—'}
                        </span>
                        <span className="pe-col pe-metal-readout pe-metal-value">
                          {val ? formatCurrency(val) : '—'}
                        </span>
                        <button
                          className="pe-col pe-col-del pe-del-btn"
                          onClick={() => removeMetalRow(r._id)}
                          aria-label="Remove metal"
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button className="pe-add-btn" onClick={addMetalRow}>
                  + Add precious metal
                </button>

                {(cashValue > 0 || totalMetalsValue > 0) && (
                  <div className="pe-other-totals">
                    {cashValue > 0 && (
                      <span className="pe-other-total-item">
                        Cash: <strong>{formatCurrency(cashValue)}</strong>
                      </span>
                    )}
                    {totalMetalsValue > 0 && (
                      <span className="pe-other-total-item">
                        Precious metals: <strong>{formatCurrency(totalMetalsValue)}</strong>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {status && (
                <div className={`pe-status pe-status-${status.type}`}>{status.msg}</div>
              )}

              <div className="pe-actions">
                <button
                  className="pe-save-btn"
                  onClick={() => handleSave({ alsoAnalyze: false })}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Portfolio'}
                </button>
                {onAnalyze && (
                  <button
                    className="pe-save-btn pe-analyze-btn"
                    onClick={() => handleSave({ alsoAnalyze: true })}
                    disabled={saving}
                    title="Save and run conviction scoring on these holdings"
                  >
                    {saving ? 'Saving…' : 'Save & Analyze'}
                  </button>
                )}
                <span className="pe-actions-hint">
                  Saving updates what Alpha sees. "Save &amp; Analyze" runs full conviction scoring on these holdings.
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
